import React, { useEffect, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { useFeatureFlags } from '@node-space/hooks'
import Box from '@node-space/storybook-components/dist/Box'
import Button from '@node-space/storybook-components/dist/Button'
import Icon from '@node-space/storybook-components/dist/Icon'
import Loader from '@node-space/storybook-components/dist/Loader'
import PageHeader from '@node-space/storybook-components/dist/PageHeader'
import { StatusCard } from '@node-space/storybook-components/dist/StatusCard'
import { logError } from '@node-space/utils'
import TwoFAModal from 'components/modals/TwoFAModal/TwoFAModal'
import { useProfileContext } from 'hooks/context/useProfileContext'
import { useMappedRoles } from 'hooks/useMappedRoles'
import { useToastContext } from 'hooks/useToastContext'
import { useTwoFAContext } from 'hooks/useTwoFAContext'
import { reactQueryKeys } from 'reactQueryKeys/reactQueryKeys'
import { getBankAccounts } from 'services/AccountService'
import { Currencies } from 'types/types'
import { AddBankAccountModal, CurrencySelectModal, DeleteBankAccountModal } from './components'
import { BankAccountsTableV2 } from './components/BankAccountsTableV2'

const BankAccounts = () => {
  const { t } = useTranslation()
  const [loading, setLoading] = useState(false)
  const { profile } = useProfileContext()
  const { enableAcl, enableBeneficiaryConsolidation } = useFeatureFlags()

  const [bankAccounts, setBankAccounts] = useState(null)
  const [selectedCurrency, setSelectedCurrency] = useState(null)
  const [selectedAccountId, setSelectedAccountId] = useState(null)
  const [showCurrencySelectModal, setShowCurrencySelectModal] = useState(false)
  const permissions = useMappedRoles()?.manageBankAccounts

  const { twoFactorEnabled, setShowTwoFAModal } = useTwoFAContext()
  const addToast = useToastContext()

  const queryClient = useQueryClient()

  const canEdit = enableAcl ? permissions.canEdit : true

  const on2FAEnabled = () => {
    queryClient.invalidateQueries(reactQueryKeys.profile())

    addToast({ title: t('2FASuccess'), state: 'success' })
  }

  const setFields = async () => {
    setLoading(true)

    try {
      // TODO: To be moved to React Query
      const bankAccounts = await getBankAccounts()
      setLoading(false)

      let bankAccountData = []
      if (bankAccounts?.length > 0) {
        bankAccountData = bankAccounts?.map(item => {
          const currentItem = {
            id: item.id,
            currency: item.currency,
            accountHolderName: item.options.accountHolderName,
            bank: item.description,
            status: item.status,
            accountNumber: '',
            action: 'delete',
          }

          switch (item.currency) {
            case Currencies.EUR:
              currentItem.accountNumber = item?.options?.iban
              currentItem.bank = item.description
              break
            case Currencies.GBP:
              currentItem.accountNumber = item?.options?.ukAccountNumber || item?.options?.iban
              currentItem.bank = item.description
              break
            case Currencies.ZAR:
            case Currencies.NGN:
              currentItem.accountNumber = item?.options?.accountNumber
              currentItem.bank = item.description
              break
          }
          return currentItem
        })
      }
      setBankAccounts(bankAccountData)
    } catch (error) {
      logError('Error from BankAccounts - setFields', error)
      setLoading(false)
    }
  }

  useEffect(() => {
    setFields()
  }, [])

  //Control flow methods
  const onCurrencySelect = currency => {
    setShowCurrencySelectModal(false)
    setSelectedCurrency(currency)
  }

  const onBackClick = () => {
    setSelectedCurrency(null)
    setShowCurrencySelectModal(true)
  }

  const onCurrencyClose = () => {
    setSelectedCurrency(null)
    setShowCurrencySelectModal(false)
  }

  const onAddBankAccountSuccess = () => {
    onCurrencyClose()
    setFields()
  }

  const setSelectedAccount = accountId => {
    setSelectedAccountId(accountId)
  }

  const onDeleteClose = () => {
    setSelectedAccountId(null)
  }

  const onDeleteSuccess = () => {
    onDeleteClose()
    setFields()
  }

  const emptyStateMessage = enableBeneficiaryConsolidation
    ? t('bankAccount.noBankAccountsAndCannotAdd')
    : t('bankAccount.emptyStateDescription')

  return (
    <>
      <DeleteBankAccountModal
        accountId={selectedAccountId}
        onClose={onDeleteClose}
        onSuccess={onDeleteSuccess}
      />
      <CurrencySelectModal
        modalOpen={showCurrencySelectModal}
        onCurrencySelect={onCurrencySelect}
        onClose={onCurrencyClose}
      />
      <AddBankAccountModal
        selectedCurrency={selectedCurrency}
        handleBackClick={onBackClick}
        onSuccess={onAddBankAccountSuccess}
        onClose={onCurrencyClose}
      />
      <TwoFAModal onComplete={on2FAEnabled} />

      {!twoFactorEnabled && (
        <Box className="space-y-4">
          <p>{t('bankAccount.enable2FAToManageAccount')}</p>
          <Button onClick={() => setShowTwoFAModal(true)}>{t('2FAEnable')}</Button>
        </Box>
      )}

      {twoFactorEnabled &&
        (loading ? (
          <Box flex justify-center className="mt-6">
            <Loader theme="dark" size="large" />
          </Box>
        ) : !bankAccounts?.length ? (
          <StatusCard
            headline={t('bankAccount.emptyStateHeadline')}
            description={emptyStateMessage}
            button={
              canEdit && !enableBeneficiaryConsolidation
                ? {
                    text: t('bankAccount.emptyStateButtonText'),
                    onClick: () => setShowCurrencySelectModal(true),
                  }
                : null
            }
          />
        ) : (
          <>
            <PageHeader
              description={t('bankAccount.pageDescription')}
              renderRightSection={
                !!profile.twoFactorEnabled &&
                canEdit &&
                !enableBeneficiaryConsolidation && (
                  <Box className="flex-initial w-auto top-0 right-0 my-2">
                    <Button
                      disabled={loading}
                      onClick={() => setShowCurrencySelectModal(true)}
                      iconElement={<Icon name="PlusIcon" color="white" />}
                    >
                      {t('settingsBankAccountAddBankAccount')}
                    </Button>
                  </Box>
                )
              }
            />
            <Box flex direction="col">
              <BankAccountsTableV2
                bankAccounts={bankAccounts}
                setSelectedAccount={setSelectedAccount}
                canEdit={canEdit}
              />
            </Box>
          </>
        ))}
    </>
  )
}

export default BankAccounts
